// AJM receipt template — ONE renderer for every receipt the business issues.
//
// Used by the pilot's Billing → Payments download (src/lib/pdf.ts) and by the
// command-line generator (scripts/receipt.mjs <transactionId>), so a receipt Casey
// emails by hand is pixel-identical to the one the portal produces.
//
// Look: the Blackout brand spec (Branding AJM/AJM Blackout Marks/SPEC.md) put on
// paper — ink black on white, smoke for secondary type, bone for the one tinted
// band, the AJM Aviation wordmark PNG in the masthead. Type is jsPDF's
// built-in Helvetica (the spec's Futura is a print face; embedding it would add
// ~100 KB to the bundle for a document nobody sees at display sizes).
//
// Casey, 2026-09-09: "We need a template that we love and that can be repeatable."
import type { jsPDF } from 'jspdf';
import { RECEIPT_LOGO_JPG, RECEIPT_LOGO_ASPECT } from './receiptLogo';
import { TRACKED_REG } from './flightTrack';
import { OWNER_NAME, OWNER_EMAIL } from './identity';

export interface ReceiptLine {
  description: string; // "Dry lease — N8366X, 1961 Cessna 172C"
  detail?: string; // "Sep 8, 2026 · KLGB local · Hobbs 5685.0 to 5686.3"
  qty?: number; // Hobbs hours
  qtyUnit?: string; // "hr"
  rate?: number; // $/hr
  amount: number;
}

export interface ReceiptData {
  receiptNo: string;
  paidAt: Date;
  method: 'cash' | 'card';
  cardLine?: string; // "VISA ····4242" (card only)
  reference: string; // ledger id, and the Stripe id for card payments
  payer: { name: string; addressLines?: string[]; email?: string; phone?: string };
  lines: ReceiptLine[];
  total: number;
  leaseLine?: string; // "Aircraft Dry Lease Agreement signed September 2, 2026"
  receivedBy?: string; // cash only — who took the money
  note?: string; // optional one-liner under the total (e.g. away-from-base explanation)
}

export const ISSUER = {
  name: 'AJM Aviation Ventures LLC',
  addressLines: ['101 Main Street, Suite H', 'Seal Beach, CA 90740'],
  email: OWNER_EMAIL,
  web: 'ajmaviation.com',
  tagline: `Dry lease of ${TRACKED_REG} · Long Beach (KLGB)`,
};

// Palette — straight from SPEC.md.
const INK = '#101214';
const SMOKE = '#70767B';
const BONE = '#EDEDEA';
const HAIR = '#D9DBD8';
const PAPER = '#FFFFFF';

const PAGE_W = 612;
const PAGE_H = 792;
const M = 54; // 0.75in margin
const RIGHT = PAGE_W - M;

export const fmtMoney = (n: number) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtLongDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const fmtStamp = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();

/**
 * Receipt numbers are ONE running sequence for every successful payment, card or
 * cash — AJM-1001, AJM-1002, … — assigned server-side (functions/index.js
 * assignReceiptNumber) the moment a payment succeeds and stored on the ledger
 * entry as `receiptNo`. This client never invents one; an entry without a number
 * (only possible before the backfill ran) renders as "pending".
 */
export const RECEIPT_NO_PENDING = 'Pending';

/**
 * Map a ledger entry (+ its flight, the payer, the aircraft) to receipt data.
 * Structural types only, so the same function runs in the browser (Firestore
 * client Timestamps) and in node (admin Timestamps).
 */
export function receiptFromTransaction(args: {
  tx: {
    transactionId: string;
    amount: number;
    description?: string;
    paymentMethod?: 'card' | 'cash';
    status: string;
    stripeChargeId?: string;
    receiptNo?: string;
    createdAt?: { toDate?: () => Date } | null;
    cashReceivedAt?: { toDate?: () => Date } | null;
  };
  flight?: {
    date: string;
    departureAirport?: string;
    destinations?: string[];
    hobbsOut?: number;
    hobbsIn?: number;
    hoursFlown?: number;
    awayFromBaseCharge?: number;
  } | null;
  payer: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    leaseSignedAt?: Date | null;
  };
  aircraft: { tailNumber: string; makeModel?: string; hourlyRate?: number };
  cardLine?: string;
  receivedBy?: string;
}): ReceiptData {
  const { tx, flight, payer, aircraft } = args;
  const isCash = tx.paymentMethod === 'cash' || tx.transactionId.startsWith('cash_');
  const paidAt = tx.cashReceivedAt?.toDate?.() ?? tx.createdAt?.toDate?.() ?? new Date();
  const model = aircraft.makeModel || '1961 Cessna 172C';

  const lines: ReceiptLine[] = [];
  const away = flight?.awayFromBaseCharge || 0;
  const hours = flight?.hoursFlown ?? 0;
  const hoursAmount = Math.max(0, +(tx.amount - away).toFixed(2));
  const rate = hours > 0 ? +(hoursAmount / hours).toFixed(2) : aircraft.hourlyRate;
  if (flight) {
    const route = [flight.departureAirport, ...(flight.destinations ?? [])].filter(Boolean);
    const routeText = route.length > 1 ? route.join(' to ') : `${route[0] ?? 'KLGB'} local`;
    const when = new Date(`${flight.date}T12:00:00`);
    const hobbs =
      flight.hobbsOut != null && flight.hobbsIn != null
        ? ` · Hobbs ${flight.hobbsOut.toFixed(1)} to ${flight.hobbsIn.toFixed(1)}`
        : '';
    lines.push({
      description: `Aircraft dry lease — ${aircraft.tailNumber}, ${model}`,
      detail: `${fmtLongDate(when)} · ${routeText}${hobbs}`,
      qty: hours || undefined,
      qtyUnit: 'hr',
      rate,
      amount: hoursAmount,
    });
    if (away > 0) {
      lines.push({
        description: 'Away-from-base minimum',
        detail: 'Two Hobbs hours per night away from Long Beach (lease §5.3)',
        amount: away,
      });
    }
  } else {
    lines.push({ description: tx.description || 'Charge', amount: tx.amount });
  }

  const name = `${payer.firstName ?? ''} ${payer.lastName ?? ''}`.replace(/\s+/g, ' ').trim() || payer.email || 'Pilot';
  const addressLines = [
    payer.address,
    [payer.city, payer.state].filter(Boolean).join(', ') + (payer.zip ? ` ${payer.zip}` : ''),
  ].filter((s): s is string => !!s && s.trim() !== '');

  return {
    receiptNo: tx.receiptNo || RECEIPT_NO_PENDING,
    paidAt,
    method: isCash ? 'cash' : 'card',
    cardLine: isCash ? undefined : args.cardLine,
    reference: isCash ? tx.transactionId : tx.stripeChargeId || tx.transactionId,
    payer: { name, addressLines, email: payer.email, phone: payer.phone },
    lines,
    total: tx.amount,
    leaseLine: payer.leaseSignedAt
      ? `Aircraft Dry Lease Agreement signed ${fmtLongDate(payer.leaseSignedAt)}`
      : 'Aircraft Dry Lease Agreement',
    receivedBy: isCash ? args.receivedBy || OWNER_NAME : undefined,
  };
}

// ───────────────────────────── drawing ─────────────────────────────

function label(doc: jsPDF, text: string, x: number, y: number, align: 'left' | 'right' = 'left') {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(SMOKE);
  doc.setCharSpace(0.9);
  const t = text.toUpperCase();
  // jsPDF's align:'right' ignores charSpace, so measure it ourselves.
  const w = doc.getTextWidth(t) + 0.9 * (t.length - 1);
  doc.text(t, align === 'right' ? x - w : x, y);
  doc.setCharSpace(0);
}

function body(doc: jsPDF, size = 10, color = INK, weight: 'normal' | 'bold' = 'normal') {
  doc.setFont('helvetica', weight);
  doc.setFontSize(size);
  doc.setTextColor(color);
}

function hairline(doc: jsPDF, y: number, x1 = M, x2 = RIGHT, color = HAIR, width = 0.5) {
  doc.setDrawColor(color);
  doc.setLineWidth(width);
  doc.line(x1, y, x2, y);
}

/** Rotated PAID stamp — outline box, big word, the date under it. */
function paidStamp(doc: jsPDF, cx: number, cy: number, dateText: string) {
  const w = 118;
  const h = 46;
  const angle = -7; // degrees, counter-clockwise
  const rad = (angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corner = (dx: number, dy: number): [number, number] => [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
  const c = [corner(-w / 2, -h / 2), corner(w / 2, -h / 2), corner(w / 2, h / 2), corner(-w / 2, h / 2)];
  doc.setDrawColor(INK);
  doc.setLineWidth(1.6);
  const segs: [number, number][] = [];
  for (let i = 1; i < c.length; i++) segs.push([c[i][0] - c[i - 1][0], c[i][1] - c[i - 1][1]]);
  doc.lines(segs, c[0][0], c[0][1], [1, 1], 'S', true);
  // Inner keyline, the "rubber stamp" double edge.
  const inset = 3.5;
  const ci = [
    corner(-w / 2 + inset, -h / 2 + inset),
    corner(w / 2 - inset, -h / 2 + inset),
    corner(w / 2 - inset, h / 2 - inset),
    corner(-w / 2 + inset, h / 2 - inset),
  ];
  const segsI: [number, number][] = [];
  for (let i = 1; i < ci.length; i++) segsI.push([ci[i][0] - ci[i - 1][0], ci[i][1] - ci[i - 1][1]]);
  doc.setLineWidth(0.6);
  doc.lines(segsI, ci[0][0], ci[0][1], [1, 1], 'S', true);

  // Text is rotated about its own anchor, so compute anchors on the rotated frame.
  doc.setTextColor(INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setCharSpace(3);
  const wordW = doc.getTextWidth('PAID') + 3 * 3;
  const [wx, wy] = corner(-wordW / 2, 2);
  doc.text('PAID', wx, wy, { angle: -angle });
  doc.setCharSpace(0.6);
  doc.setFontSize(6.5);
  const dW = doc.getTextWidth(dateText) + 0.6 * dateText.length;
  const [dx, dy] = corner(-dW / 2, 14);
  doc.text(dateText, dx, dy, { angle: -angle });
  doc.setCharSpace(0);
}

export function renderReceipt(doc: jsPDF, d: ReceiptData) {
  doc.setFillColor(PAPER);
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F');

  // ── Masthead ────────────────────────────────────────────────────────
  // The AJM Aviation wordmark (Casey, 9/9/26: the receipt carries the real logo,
  // not the blackout delta). Height 36pt; width follows the PNG's aspect.
  const logoH = 36;
  const logoW = logoH * RECEIPT_LOGO_ASPECT;
  doc.addImage(RECEIPT_LOGO_JPG, 'JPEG', M - 2, M - 8, logoW, logoH);
  body(doc, 8, SMOKE);
  doc.text(ISSUER.tagline, M, M + 40);

  body(doc, 26, INK, 'bold');
  doc.setCharSpace(4);
  doc.text('RECEIPT', RIGHT - (doc.getTextWidth('RECEIPT') + 4 * 6), M + 16);
  doc.setCharSpace(0);

  // Meta block, right-aligned: label column + value column.
  const metaY = M + 40;
  const rows: [string, string][] = [
    ['Receipt no.', d.receiptNo],
    ['Date paid', fmtLongDate(d.paidAt)],
    ['Payment', d.method === 'cash' ? 'Cash' : `Card${d.cardLine ? ` · ${d.cardLine}` : ''}`],
  ];
  rows.forEach(([k, v], i) => {
    const y = metaY + i * 15;
    label(doc, k, RIGHT - 118, y, 'right');
    body(doc, 10, INK);
    doc.text(v, RIGHT, y, { align: 'right' });
  });

  hairline(doc, M + 96, M, RIGHT, INK, 0.8);

  // ── Parties ─────────────────────────────────────────────────────────
  let y = M + 118;
  label(doc, 'Received from', M, y);
  label(doc, 'Issued by', 330, y);
  y += 15;
  body(doc, 11, INK, 'bold');
  doc.text(d.payer.name, M, y);
  doc.text(ISSUER.name, 330, y);
  y += 14;
  body(doc, 9.5, INK);
  const left = [...(d.payer.addressLines ?? [])];
  const right = [...ISSUER.addressLines];
  const n = Math.max(left.length, right.length);
  for (let i = 0; i < n; i++) {
    if (left[i]) doc.text(left[i], M, y);
    if (right[i]) doc.text(right[i], 330, y);
    y += 12.5;
  }
  body(doc, 9, SMOKE);
  const contactL = [d.payer.email, d.payer.phone].filter(Boolean) as string[];
  const contactR = [ISSUER.email];
  const m2 = Math.max(contactL.length, contactR.length);
  for (let i = 0; i < m2; i++) {
    if (contactL[i]) doc.text(contactL[i], M, y);
    if (contactR[i]) doc.text(contactR[i], 330, y);
    y += 12;
  }

  // ── Line items ──────────────────────────────────────────────────────
  y += 16;
  const colHobbs = 372;
  const colRate = 452;
  doc.setFillColor(BONE);
  doc.rect(M, y, RIGHT - M, 20, 'F');
  label(doc, 'Description', M + 8, y + 13);
  label(doc, 'Hobbs', colHobbs, y + 13, 'right');
  label(doc, 'Rate', colRate, y + 13, 'right');
  label(doc, 'Amount', RIGHT - 8, y + 13, 'right');
  y += 20;

  for (const line of d.lines) {
    const rowTop = y;
    y += 15;
    body(doc, 10, INK, 'bold');
    doc.text(line.description, M + 8, y);
    body(doc, 10, INK);
    if (line.qty != null) doc.text(`${line.qty.toFixed(1)} ${line.qtyUnit ?? ''}`.trim(), colHobbs, y, { align: 'right' });
    if (line.rate != null) doc.text(`${fmtMoney(line.rate)}/${line.qtyUnit ?? 'hr'}`, colRate, y, { align: 'right' });
    doc.text(fmtMoney(line.amount), RIGHT - 8, y, { align: 'right' });
    if (line.detail) {
      y += 12;
      body(doc, 8.5, SMOKE);
      doc.text(line.detail, M + 8, y);
    }
    y += 10;
    hairline(doc, y, M, RIGHT);
    void rowTop;
  }

  // ── Totals ──────────────────────────────────────────────────────────
  y += 18;
  const totalsLabelX = 318;
  body(doc, 10, SMOKE);
  doc.text('Total', totalsLabelX, y);
  body(doc, 10, INK);
  doc.text(fmtMoney(d.total), RIGHT - 8, y, { align: 'right' });
  y += 15;
  body(doc, 10, SMOKE);
  doc.text(
    d.method === 'cash' ? `Paid — cash, ${fmtLongDate(d.paidAt)}` : `Paid — card, ${fmtLongDate(d.paidAt)}`,
    totalsLabelX,
    y
  );
  body(doc, 10, INK);
  doc.text(`– ${fmtMoney(d.total)}`, RIGHT - 8, y, { align: 'right' });
  y += 8;
  hairline(doc, y, totalsLabelX, RIGHT, INK, 0.8);
  y += 16;
  body(doc, 11.5, INK, 'bold');
  doc.text('Balance due', totalsLabelX, y);
  doc.text(fmtMoney(0), RIGHT - 8, y, { align: 'right' });

  // Stamp sits left of the totals, on the same band.
  paidStamp(doc, M + 100, y - 14, fmtStamp(d.paidAt));

  // ── Terms / thanks ──────────────────────────────────────────────────
  y += 44;
  body(doc, 9.5, INK);
  doc.text(`Thank you for flying ${d.lines[0]?.description.includes(TRACKED_REG) ? TRACKED_REG : 'with AJM'}.`, M, y);
  y += 14;
  body(doc, 8.5, SMOKE);
  const terms: string[] = [];
  if (d.leaseLine) terms.push(`Issued under the ${d.leaseLine}. Hobbs time and charges as logged in the AJM portal by the pilot.`);
  if (d.method === 'cash')
    terms.push(`Cash received in person by ${d.receivedBy ?? 'AJM Aviation Ventures LLC'}. No card was charged for this flight.`);
  if (d.note) terms.push(d.note);
  terms.push('Payment history and a copy of this receipt: app.ajmaviation.com/billing');
  for (const t of terms) {
    const wrapped = doc.splitTextToSize(t, RIGHT - M) as string[];
    doc.text(wrapped, M, y);
    y += 11.5 * wrapped.length;
  }

  // ── Footer — reads like a signature block, not a legal line ────────
  const fy = PAGE_H - 78;
  hairline(doc, fy - 14, M, RIGHT);
  body(doc, 8.5, INK, 'bold');
  doc.text(ISSUER.name, M, fy);
  const kv = (k: string, v: string, yy: number) => {
    body(doc, 8, INK, 'bold');
    doc.text(k, M, yy);
    body(doc, 8, SMOKE);
    doc.text(v, M + 12, yy);
  };
  kv('A', ISSUER.addressLines.join(', '), fy + 13);
  kv('E', ISSUER.email, fy + 25);
  kv('W', ISSUER.web, fy + 37);
  body(doc, 8, SMOKE);
  doc.text('Page 1 of 1', RIGHT, fy, { align: 'right' });
  body(doc, 7, SMOKE);
  doc.text(`Ref ${d.reference}`, RIGHT, fy + 13, { align: 'right' });
}

export function receiptFilename(d: ReceiptData) {
  const no = d.receiptNo === RECEIPT_NO_PENDING ? d.paidAt.toISOString().slice(0, 10) : d.receiptNo;
  return `AJM-receipt-${no}.pdf`;
}
